/* eslint-disable no-prototype-builtins */
import moment, { Moment } from "moment";
import "moment/locale/ro";
import { ResolutionBreakPoints } from "./Constants";
import { ETaskStatus, EWordpressStockStatus } from "./Enums";
import { Vocabulary } from "./Vocabulary";
import htmlToDraft from "html-to-draftjs";
import { ContentState, EditorState } from "draft-js";
import axios, { AxiosResponse } from "axios";
import { endpoints } from "./UrlEnum";
import { toast } from "react-toastify";
import Config from "./Config";

moment.locale("ro");

/**
 *
 * @param model
 * @param propertiesArray
 * @returns
 */
export const generateFormData = (model: any) => {
  const formData = new FormData();

  for (const property in model) {
    if (typeof model[property] === "boolean")
      formData.append(property, model[property]);
    else formData.append(property, model[property] ? model[property] : null);
  }

  return formData;
};

/**
 * Validates if a date string is in a valid date format (YYYY-MM-DD or YYYY-MM-DD HH:MM:SS)
 * Ensures it's not just a year and can be properly parsed
 * @param dateString - The date string to validate
 * @returns true if the date is valid, false otherwise
 */
export const isValidDate = (dateString: string): boolean => {
  if (!dateString) return false;

  // Check if date is in DD.MM.YYYY or DD.MM.YY format (e.g., "13.09.2026" or "13.09.26")
  const ddmmyyyyRegex = /^(\d{2})\.(\d{2})\.(\d{2}|\d{4})$/;
  const ddmmyyyyMatch = dateString.match(ddmmyyyyRegex);
  if (ddmmyyyyMatch) {
    const [, day, month, yearPart] = ddmmyyyyMatch;
    const year = yearPart.length === 2 ? `20${yearPart}` : yearPart;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return !isNaN(date.getTime()) &&
           date.getDate() === parseInt(day) &&
           date.getMonth() === parseInt(month) - 1 &&
           date.getFullYear() === parseInt(year);
  }

  // Check if date is in YYYY-MM-DD format (e.g., "2026-09-13")
  const yyyymmddRegex = /^\d{4}(-\d{2}(-\d{2})?)?/;
  if (!yyyymmddRegex.test(dateString)) return false;

  // If it's just a year, it's not a valid date
  if (dateString.length === 4) return false;

  // Try to parse as date to ensure it's valid
  const date = new Date(dateString);
  return !isNaN(date.getTime()) && dateString.startsWith(date.getFullYear().toString());
};

/**
 *
 */
export function logout() {
  localStorage.removeItem("clientId");
  localStorage.removeItem("fullName");
  localStorage.removeItem("access_token");
  localStorage.removeItem(`${Config.cache_prefix}storage`);
  localStorage.removeItem("userName");
  localStorage.removeItem("userRoles");
  localStorage.removeItem("managements");
  localStorage.removeItem("orderOfferPassword");
}

/**
 *
 * @param model Main model (object)
 * @param completedData The object that is completed and can have undefined props
 * @returns New Filtered Object (Return is present to be easier to understand what the function will return)
 */
export const mergeObjects = (model: any, completedData: any) => {
  for (const idx in completedData) {
    if (completedData[idx] !== undefined) model[idx] = completedData[idx];
  }
  return model;
};

/**
 *
 * @param discount
 * @param quantity
 * @param unitPrice
 * @returns
 */
export const calcValue = (discount: any, quantity: any, unitPrice: any) => {
  const totalPrice = parseFloat(quantity) * parseFloat(unitPrice);
  const totalPriceWithDiscount =
    (parseFloat(quantity) * parseFloat(unitPrice) * parseFloat(discount)) / 100;
  const total = totalPrice - totalPriceWithDiscount;

  return isNaN(total) ? 0 : total;
};

/**
 *
 * @param wordpressPrice
 * @param tva
 * @returns
 */
export const calcPriceWithoutTVA = (wordpressPrice: any, tva: any) => {
  const priceWithoutTVA =
    (parseFloat(wordpressPrice) * 10000) /
    ((1 + parseFloat(tva) / 100) * 10000);
  return isNaN(priceWithoutTVA) ? 0 : priceWithoutTVA;
};

/**
 *
 * @param wordpressPrice
 * @param tva
 * @returns
 */
export const calcTVAValue = (wordpressPrice: any, tva: any) => {
  const priceWithoutTVA =
    wordpressPrice - parseFloat(wordpressPrice) / (1 + parseFloat(tva) / 100);
  return isNaN(priceWithoutTVA) ? 0 : priceWithoutTVA;
};

/**
 *
 * @param priceWithoutTVA
 * @param tva
 * @returns
 */
export const calcTVAValueFromPriceWithoutTVA = (
  priceWithoutTVA: any,
  tva: any
) => {
  const newPriceWithoutTVA = (tva / 100) * priceWithoutTVA;
  return isNaN(newPriceWithoutTVA) ? 0 : newPriceWithoutTVA;
};
/**
 *
 * @param value
 * @param tva
 * @returns
 */
export const calcValueWithTVA = (value: any, tva: any) => {
  const valueWithTVA =
    parseFloat(value) + parseFloat(value) * (parseFloat(tva) / 100);
  return isNaN(valueWithTVA) ? 0 : valueWithTVA;
};

/**
 *
 * @param price
 * @param tva
 * @returns
 */
export const calculatePriceWithTVA = (price: any, tva: any) => {
  const valueWithTVA =
    parseFloat(price) + parseFloat(price) * (parseFloat(tva) / 100);
  if (isNaN(valueWithTVA)) return 0;
  //  check number of decimals in valueWithTVA
  const decimals = valueWithTVA.toString().split(".")[1]?.length;
  //if the number of decimals is greater than 2, round the value
  return decimals > 2 ? getRoundedValue(valueWithTVA, 2) : valueWithTVA;
  // return valueWithTVA;
};

/**
 *
 * @param value
 * @param decimals
 * @returns
 */
const getRoundedValue = (value: any, decimals: number) => {
  if (isNaN(value)) return 0;
  return (Math.round((value + Number.EPSILON) * 100) / 100).toFixed(decimals);
};

/**
 *
 * @param unitPrice
 * @param quantity
 * @returns
 */
export const calculateTotal = (unitPrice: any, quantity: any) => {
  //parse values to float
  const unitPriceFloat = parseFloat(unitPrice);
  const quantityFloat = parseFloat(quantity);
  //calculate total
  const total = unitPriceFloat * quantityFloat;
  //return total
  return isNaN(total) ? 0 : total;
};

/**
 *
 * @param discount
 * @param quantity
 * @param unitPrice
 * @returns
 */
export const calcDiscount = (discount: any, quantity: any, unitPrice: any) => {
  const subTotal =
    (parseFloat(quantity) * parseFloat(unitPrice) * parseFloat(discount)) / 100;
  return isNaN(subTotal) ? 0 : subTotal;
};

/**
 *
 * @param file
 * @returns
 */
export const resizeImage = (file: any) => {
  const promise = new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = function (readerEvent) {
        const image = new Image();
        image.src = readerEvent.target?.result as string;
        image.onload = function (imageEvent) {
          const imageFile = resize(image, file.name);
          resolve(imageFile);
        };
        image.onerror = function (err) {
          reject(err);
        };
      };
      reader.readAsDataURL(file);
    } catch (e) {
      reject(e);
    }
  });
  return promise;
};

/**
 *
 * @param image
 * @param fileName
 * @returns
 */
const resize = (image: any, fileName: any) => {
  const canvas = document.createElement("canvas");
  let { width } = image,
    { height } = image;
  const maxWidth = width > 200 ? 200 : width;
  if (width > height) {
    if (width > maxWidth) {
      height *= maxWidth / width;
      width = maxWidth;
    }
  } else {
    if (height > maxWidth) {
      width *= maxWidth / height;
      height = maxWidth;
    }
  }
  canvas.width = width;
  canvas.height = height;
  canvas?.getContext("2d")?.drawImage(image, 0, 0, width, height);
  const dataUrl = canvas.toDataURL("image/png");
  const resizedImage = dataURLToBlob(dataUrl);
  return blobToFile(resizedImage, fileName);
};

/**
 *
 * @param {*} dataURL
 * @param {*} fileName
 * @returns
 */
export function dataURLToBlob(dataURL: any) {
  const BASE64_MARKER = ";base64,";

  let parts = dataURL.split(BASE64_MARKER);
  let contentType = parts[0].split(":")[1];
  let raw = window.atob(parts[1]);
  const rawLength = raw.length;

  if (dataURL.indexOf(BASE64_MARKER) === -1) {
    parts = dataURL.split(",");
    contentType = parts[0].split(":")[1];
    raw = parts[1];
    return new Blob([raw], { type: contentType });
  }
  const uInt8Array = new Uint8Array(rawLength);
  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }
  const blob = new Blob([uInt8Array], { type: contentType });
  return blob;
}

/**
 *
 * @param {*} ab
 * @param {*} fileName
 * @returns
 */
function blobToFile(ab: any, fileName: any) {
  const f = new File([ab], fileName, {
    type: "image/jpeg",
    lastModified: new Date().getTime(),
  });
  return f;
}

/**
 *
 * @param file
 * @returns
 */
export const toBase64 = (file: any) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

/**
 *
 * @param routePath
 * @param currentPath
 * @returns
 */
export const mainPathMatches = (routePath: string, currentPath: any) => {
  if (routePath?.endsWith("?")) {
    // If the route path ends with '?', check only the part before the '?'
    return currentPath?.startsWith(routePath.slice(0, -1));
  }
  //id the route contains / check only the first part of the path
  if (
    routePath?.includes("/") &&
    routePath?.length > 3 &&
    routePath?.split("/").length > 2
  ) {
    return currentPath?.startsWith(`/${routePath.split("/")[1]}`);
  }
  if (currentPath.includes("/") && routePath?.length > 3) {
    return currentPath?.includes(routePath);
  }

  // Otherwise, check the whole path
  return currentPath === routePath;
};

/**
 *
 * @param routePath
 * @param currentPath
 * @returns
 */
export const checkSecondaryPathsMatch = (
  routePath: string,
  currentPath: any
) => {
  if (routePath.endsWith("?")) {
    // If the route path ends with '?', check only the part before the '?'
    // return currentPath.startsWith(routePath.slice(0, -1));
  }
  // Otherwise, check the whole path
  return currentPath === routePath;
};

/**
 *
 * @param date
 * @param format
 * @returns
 */
export const formatStringDate = (
  date: string | Date | Moment | null | undefined,
  format: any
) => {
  if (!date) return null;
  return moment(date).format(format);
};

/**
 *
 * @param url
 */
export const openInNewTab = (url: string): void => {
  const newWindow = window.open(url, "_blank", "noopener,noreferrer");
  if (newWindow) newWindow.opener = null;
};

/**
 *
 * @param timestamp
 * @param format
 * @returns
 */
export const formatTimestamp = (timestamp: any, format: any) => {
  const timestampToDate = new Date(timestamp * 1000);
  const response = moment(timestampToDate).format(format);
  return response && response !== "Invalid date" ? response : "";
};

/**
 * checks if device is mobile
 * @returns
 */
export const isMobile = () => {
  return document.body.offsetWidth <= ResolutionBreakPoints.sm;
};

/**
 * check if device is tablet
 * @returns
 */
export const isTablet = () => {
  return document.body.offsetWidth <= ResolutionBreakPoints.lg;
};

/**
 *
 * @param backgroundColor
 * @returns
 */
export const setFontColorBasedOnBackground = (backgroundColor: string) => {
  let sum = 0;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    backgroundColor
  );

  sum = result
    ? parseInt(result[1], 16) / 2 +
    parseInt(result[2], 16) / 2 +
    parseInt(result[3], 16) / 2
    : 0;
  if (sum > 127 || sum === 0) {
    return "#000";
  }
  return "#fff";
};
/**
 *
 * @param str
 * @returns
 */
export const replaceAllUnescapedChars = (str: string) => {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")
    .replace(/ /g, "&nbsp;");
};

/**
 *
 * @param str
 * @returns
 */
export const restoreAllEscapedChars = (str: string) => {
  return str
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&nbsp;/g, " ");
};

/**
 *
 * @param html
 * @returns
 */
export const decodeHtml = (html: string) => {
  const e = document.createElement("div");
  e.innerHTML = html;
  return e.childNodes.length === 0 ? "" : e.childNodes[0].nodeValue;
};

/**
 *
 * @param html
 * @returns
 */
export const extractStringFromHtml = (html: string): string => {
  // Create a temporary container element
  const container = document.createElement("div");
  // Set the HTML content of the container
  container.innerHTML = html;

  // Function to recursively traverse through the DOM nodes
  const getText = (node: any) => {
    let text = "";
    // Iterate over child nodes
    node.childNodes.forEach((child: any) => {
      // If it's a text node, append its content
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent;
      } else if (
        child.nodeType === Node.ELEMENT_NODE &&
        child.tagName.toLowerCase() !== "style"
      ) {
        // If it's an element node and not a <style> tag, recursively get its text content
        text += getText(child);
      }
    });
    return text;
  };

  // Call the getText function starting from the container element
  return getText(container);
};

/**
 *
 * @param str
 * @returns
 */
export const isStringOnlyHTML = (str: string) => {
  const fragment = document.createRange().createContextualFragment(str);

  // remove all non text nodes from fragment
  fragment.querySelectorAll("*").forEach((el: any) => {
    if (el.childNodes.length === 0) {
      el.parentNode.removeChild(el);
    }
  });

  // if there is textContent, then not a pure HTML
  return !(fragment.textContent || "").trim();
};

/**
 *
 * @param html
 * @returns
 */
export const getHtml = (html: string) => {
  const contentBlock = htmlToDraft(html);
  const contentState = ContentState.createFromBlockArray(
    contentBlock.contentBlocks
  );
  return EditorState.createWithContent(contentState);
};

/**
 *
 * @param html
 * @returns
 */
export const sanitizeHtml = (html: string): string => {
  // Create a DOMParser to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Remove script elements
  const scriptElements = doc.querySelectorAll("script");
  scriptElements.forEach((script) => {
    script.parentNode?.removeChild(script);
  });

  // Remove link elements (including their attributes)
  // const linkElements = doc.querySelectorAll("link");
  // linkElements.forEach((link) => {
  //   link.parentNode?.removeChild(link);
  // });

  // Serialize the cleaned DOM back to a string
  const cleanedHtml = new XMLSerializer().serializeToString(doc);

  return cleanedHtml;
};

/**
 *
 * @param users
 * @returns
 */
export const mapUserListToChatSuggestions = (users: any) => {
  return users?.map((participant: any) => {
    return {
      text: `${participant.user.lastName || ""} ${participant.user.firstName || ""
        }`,
      value: `${participant.user.lastName || ""} ${participant.user.firstName || ""
        }`,
      url: "",
    };
  });
};

export const checkIfStringContainsOnlyOneChar = (str: string, char: string) => {
  if (!str) return null;
  //remove spaces from string
  str = str.replace(/\s/g, "");
  //check if string contains only one char
  return str.length === 1 && str === char ? null : str;
};

/**
 *
 * @param stockStatus
 * @returns
 */
export const renderStockMessage = (stockStatus: string | null) => {
  switch (stockStatus) {
    case EWordpressStockStatus.OUT_OF_STOCK:
      return Vocabulary.outOfStock;
    case EWordpressStockStatus.IN_STOCK:
      return Vocabulary.inStock;
    case EWordpressStockStatus.ON_BACKORDER:
      return Vocabulary.onBackOrder;
    default:
      return Vocabulary.inStock;
  }
};

/**
 * Make an HTTP request using Axios.
 * @param url The URL to send the request to.
 * @param method The HTTP method to use (e.g., 'GET', 'POST', etc.).
 * @param token The authorization token to include in the request headers.
 * @param body The request body (optional).
 * @returns A promise that resolves to the AxiosResponse.
 */
export const makeRequest = async (
  url: string,
  method: string,
  token: string,
  body?: any
): Promise<AxiosResponse<any>> => {
  try {
    const response = await axios({
      url,
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      data: body,
    });
    return response;
  } catch (error) {
    // Handle error (e.g., log it or throw a custom error)
    console.error("Request failed:", error);
    throw error; // Rethrow the error to propagate it up the call stack
  }
};

/**
 *
 * @param name
 * @returns
 */
export const stringAvatar = (name: string) => {
  if (!name) return null;
  const nameSplit = name?.split(" ");
  return {
    children: `${!nameSplit.length ? "" : nameSplit?.[0]?.[0] || ""}${nameSplit?.[1]?.[0] || ""
      }`,
  };
};

/**
 *
 * @param obj
 */
export const removeNullProperties = (obj: { [key: string]: any; }) => {
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (
        obj[key] === null ||
        (Array.isArray(obj[key]) && obj[key].length === 0)
      ) {
        delete obj[key];
      } else if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
        // Recursively call the function for nested objects
        removeNullProperties(obj[key]);
        // After recursive call, delete the property if the nested object is empty
        if (checkIfObjectIsEmpty(obj[key])) {
          delete obj[key];
        }
      }
    }
  }
};

/**
 *
 * @param obj
 * @returns
 */
export const checkIfObjectIsEmpty = (obj: any) => {
  return Object.keys(obj).length === 0;
};

/**
 *
 * @param chat
 * @returns
 */
export const checkIfChatHasUnreadMessages = (chat: any) => {
  //check if chat has messages and lastRead is smaller than the curent timestamp
  const lastRead = chat?.lastRead;
  //get last message timestamp
  const lastMessageTimestamp =
    chat?.messages?.[chat?.messages?.length - 1]?.timestamp;
  return chat?.messages?.length > 0 && lastRead < lastMessageTimestamp;
};

/**
 *
 * @param property
 * @returns
 */
export const checkIfPropertyIsString = (property: any) => {
  return typeof property === "string" || property instanceof String;
};

/**
 * test if file is image
 * @param file
 * @returns
 */
export const isImage = (file: any) => {
  const ext = file.name.substring(file.name.lastIndexOf(".") + 1).toLowerCase();
  return ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "gif";
};

/**
 *
 * @param text
 * @returns
 */
export const getNumberOfLinesFromText = (text: string | null | undefined) => {
  if (text) {
    const splittedText = text.split(/\r\n|\r|\n/);
    let numLines = splittedText.length;
    splittedText.forEach((line: string) => {
      if (line.length > 90) {
        numLines += line.length / 90;
      }
    });
    return numLines;
  }
  return 2;
};

/**
 *
 * @param file
 */
export const downloadFile = (file: any) => {
  fetch(`${endpoints.media.public}?path=${file?.path}`)
    .then((response) => response.blob())
    .then((response) => {
      if (response.size === 0) {
        toast.warn(Vocabulary.missingFile);
        return;
      }
      const href = URL.createObjectURL(response);
      // create "a" HTML element with href to file & click
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", file.name); //or any other extension
      document.body.appendChild(link);
      link.click();
      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    });
};

/**
 *
 * @param file
 * @param type
 * @returns
 */
export const checkFileType = (file: any, type: string) => {
  const ext = file.name.substring(file.name.lastIndexOf(".") + 1).toLowerCase();
  return ext === type;
};

/**
 *
 * @param model
 */
export const onFormError = (model: any) => {
  console.log(model);
};

/**
 *
 * @param task
 * @returns
 */
export const checkIfTaskIsLate = (task: any) => {
  //check if task end date is before today and status is not finished
  if (!task.status || task.status.name === ETaskStatus.CUSTOM) return false;
  const endDate = new Date(task.end);
  const finishDate = task.finishDate ? new Date(task.finishDate) : null;
  return (
    (moment(task.end).isBefore(moment().subtract(1, "days").endOf("day")) &&
      task.status?.name !== ETaskStatus.FINISHED) ||
    (finishDate && finishDate > endDate)
  );
};

/**
 *
 * @param obj
 * @param excludedFields
 * @returns
 */
export const filterFields = (obj: any, excludedFields: Array<string>) => {
  return Object.keys(obj).filter((key) => !excludedFields.includes(key));
};

/**
 *
 * @param bgColor
 * @returns
 */
export const getTextColorBasedOnBg = (color: string) => {
  const hex = color.replace("#", "");

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 128 ? "black" : "white";
};

export const talkToChat = async (api: string, formData: FormData) => {
  const userToken = localStorage.getItem("access_token");
  const chatUrl =
    process.env.REACT_APP_PYTHON_API_URL ||
    "http://192.168.100.109";
  const instance = axios.create({
    baseURL: chatUrl,
  });

  const response = await instance.post(api, formData, {
    headers: {
      authorization: `Bearer ${userToken}`,
    },
  });

  return response;
};

/**
 * Decode JWT token and return payload
 */
export const decodeJWTToken = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

/**
 * Check if user has certificate from JWT token
 */
export const hasUserCertificate = (): boolean => {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) return false;

    const payload = decodeJWTToken(token);
    return payload?.isCertificate === true;
  } catch (error) {
    console.error('Error checking certificate status:', error);
    return false;
  }
};

/**
 * Check if user has free subscription from JWT token
 */
export const isFreeSubscription = (): boolean => {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) return false;

    const payload = decodeJWTToken(token);
    return payload?.isFreeSubscription === true;
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return false;
  }
};

/**
 * Decode JWT token and return payload
 */


/**
 * Check if user has certificate from JWT token
 */


export interface RagResponseHistory {
  _id: string;
  datetime: string;
  messages: {
    prompt: string;
    answer: string;
  }[];
  model: string;
  dataset: string;
  datasets: string[];
  history: any;
}

export const getUserChatHistory = async (): Promise<RagResponseHistory | null> => {
  const userToken = localStorage.getItem("access_token");
  const PYTHON_API_URL = process.env.REACT_APP_CHAT_APP_URL || "https://chestionare.auto.sigurlavolan.ro";
  const userId = localStorage.getItem("clientId");
  if (!userId) return null;
  try {
    const res = await axios.get<RagResponseHistory>(`${PYTHON_API_URL}/api/client/rag-query-history/${userId}`,
      { headers: { Authorization: `Bearer ${userToken}` } }
    );

    return res.data;
  } catch (error) {
    console.error('Failed to fetch history:', error);
    return null;
  }
};

/**
 * Normalize a phone number by prefixing it with the Config.phonePrefix
 * and removing any non-digit characters.
 * @param {string} phone the phone number to normalize
 * @returns {string} the normalized phone number
 */
export function normalizePhone(phone: string | null | undefined) {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, ""); // remove non-digits

  if (cleaned?.startsWith(Config.phonePrefix)) {
    return cleaned;
  } else if (cleaned.startsWith("0")) {
    return Config.phonePrefix + cleaned.substring(1);
  } else {
    return Config.phonePrefix + cleaned;
  }
}

/**
 * Get setting by name from API
 * @param settingName The name of the setting to retrieve
 * @returns Promise<any> The setting data or null if not found
 */
export const getSettingByName = async (settingName: string): Promise<any> => {
  try {
    const { getData } = await import("../Services/getData");
    const response = await getData(`${endpoints.setting.main}${settingName}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to get setting ${settingName}:`, error);
    return null;
  }
};

/**
 * Get order advance amount from settings and compare with product price
 * @param productPrice The price of the selected product
 * @returns Promise<number> The amount to pay (either advance amount or product price)
 */
export const getOrderAdvanceAmount = async (
  productPrice: number
): Promise<number> => {
  try {
    const advanceSetting = await getSettingByName("order-advance");
    const advanceAmount = advanceSetting?.value
      ? parseFloat(advanceSetting.value)
      : 0;

    // If advance amount is bigger than product price, keep the product price
    return advanceAmount > 0 && advanceAmount > productPrice
      ? productPrice
      : advanceAmount;
  } catch (error) {
    console.error("Failed to get order advance amount:", error);
    return productPrice; // Fallback to product price on error
  }
};

/**
 * Read a cookie
 * @param {String} name
 */
export const readCookie = (name: string) => {
  const n = `${name}=`;
  const index = document.cookie.indexOf(n);
  if (index < 0) {
    return "";
  }
  let cookieValue = document.cookie.substring(index + n.length);
  const end = cookieValue.indexOf(";");
  if (end >= 0) {
    cookieValue = cookieValue.substring(0, end);
  }
  return cookieValue;
};

/**
 * Checks if a value is a string.
 * @param value
 * @returns true if the value is a string
 */
export const isString = (value: any) => typeof value === "string";

/**
 * Checks if a value is defined.
 * A value is considered defined if it is not undefined, null, or an empty string.
 *
 * @param value - The value to be checked.
 * @returns true if the value is defined, false otherwise.
 */

export const isDefined = (value: any) => {
  if (value === undefined || value === null) return false;
  if (isString(value) && value.trim() === "") return false;
  if (Array.isArray(value) && value.length === 0) return false;
  if (
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  )
    return false;
  return true;
};

/**
 * Appends a form data object with the given data.
 * If the data contains an object, it will be stringified using JSON.stringify.
 * If the data contains a File, it will be appended to the form data as is.
 * If a parentKey is provided, it will be used to construct the form key.
 * @param formData - The form data object to append to.
 * @param data - The data to append to the form data.
 * @param {string} parentKey - The parent key to use when constructing the form key. Defaults to an empty string.
 */
export default function appendFormData(
  formData: any,
  data: any,
  parentKey = ""
) {
  for (const key in data) {
    if (data.hasOwnProperty(key)) {
      const value = data[key];
      const formKey = parentKey ? `${parentKey}[${key}]` : key;

      if (value instanceof File) {
        formData.append(formKey, value);
      } else if (typeof value === "object" && value !== null && !(value instanceof File)) {
        formData.append(formKey, JSON.stringify(value));
      } else {
        formData.append(formKey, value);
      }
    }
  }
}

/**
 * Removes diacritics from a string.
 * @param {string} str - The string to remove diacritics from.
 * @returns {string} The string with diacritics removed.
 */
export function removeDiacritics(str : string) {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}